import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import CartDrawer from '@/components/CartDrawer';
import SocialLinksRow from '@/components/SocialLinksRow';
import { Disc3, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArtistInfoPage = () => {
  const [cartOpen, setCartOpen] = useState(false);
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [pageRes, settingsRes] = await Promise.all([
          axios.get(`${API}/pages/artist`),
          axios.get(`${API}/settings`),
        ]);
        setPage(pageRes.data);
        setSettings(settingsRes.data || {});
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#09090B] text-white">
      <SiteHeader onCartClick={() => setCartOpen(true)} />

      <main className="flex-1 max-w-4xl mx-auto w-full px-6 lg:px-10 py-16">
        <div className="text-center mb-14">
          {settings.artist_photo_url ? (
            <img
              src={settings.artist_photo_url}
              alt="Kehele Keff"
              className="w-40 h-40 md:w-48 md:h-48 rounded-full mx-auto mb-6 object-cover border-2 border-[#00F0FF]/30 shadow-[0_0_40px_rgba(0,240,255,0.15)]"
              data-testid="artist-photo"
            />
          ) : (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 mb-6">
              <Disc3 className="text-[#00F0FF]" size={28} />
            </div>
          )}
          <h1 className="text-4xl md:text-5xl font-heading font-medium tracking-tight mb-3" data-testid="artist-title">
            {page?.title || 'Kehele Keff'}
          </h1>
          <p className="text-[#A1A1AA] uppercase tracking-[0.25em] text-xs">DJ · Producer · Editor</p>
        </div>

        <div className="bg-[#121214] border border-[#27272A] rounded-lg p-8 md:p-10 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="text-[#00F0FF] animate-spin" size={28} />
            </div>
          ) : (
            <section data-testid="artist-body">
              <h2 className="text-xl font-heading font-semibold mb-3">About</h2>
              <div className="text-[#A1A1AA] leading-relaxed whitespace-pre-wrap text-[15px]">
                {page?.body || 'Media kit and full artist bio will be added soon.'}
              </div>
            </section>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-[#27272A]">
            <div>
              <div className="text-xs text-[#A1A1AA] uppercase tracking-[0.2em] mb-1">Based in</div>
              <div className="text-sm">Philippines</div>
            </div>
            <div>
              <div className="text-xs text-[#A1A1AA] uppercase tracking-[0.2em] mb-1">Style</div>
              <div className="text-sm">House · Techno · Bootlegs</div>
            </div>
            <div>
              <div className="text-xs text-[#A1A1AA] uppercase tracking-[0.2em] mb-1">Payment</div>
              <div className="text-sm">Maya · QRPh · Cards</div>
            </div>
          </div>

          <div className="pt-4 border-t border-[#27272A]">
            <div className="text-xs text-[#A1A1AA] uppercase tracking-[0.2em] mb-3">Connect</div>
            <SocialLinksRow align="start" />
          </div>
        </div>
      </main>

      <SiteFooter />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
};

export default ArtistInfoPage;
